<template>
    <div class="nacex-address-block">
        <div class="p-d-flex p-jc-between p-mb-2">
            <h4 class="section-title p-m-0">{{ title }}</h4>
            <div class="p-d-flex p-jc-between p-mb-2">
                <template v-if="!prefillOnly">
                    <Button v-for="acc in nacexAccounts" :key="'acc-btn-' + acc.id" :label="acc.name"
                        class="p-button-secondary p-button-info p-button-sm p-mr-2 p-button-auto"
                        :class="{'p-button-outlined': selectedAccountId !== acc.id}"
                        @click.prevent="selectAccount(acc)" />
                </template>
                <template v-else>
                    <Button v-for="acc in nacexAccounts" :key="'acc-prefill-btn-' + acc.id" :label="acc.name"
                        class="p-button-secondary p-button-info p-button-sm p-mr-2 p-button-auto"
                        :class="{'p-button-outlined': selectedPrefill !== 'account-' + acc.id}"
                        @click.prevent="fillWithAccount(acc)" />
                </template>

                <Button v-if="locations.clientData" label="Cliente"
                    class="p-button-secondary p-button-info p-button-sm p-mr-2 p-button-auto"
                    :class="{'p-button-outlined': selectedPrefill !== 'client'}"
                    @click.prevent="fillWith('clientData')" />
                <Button v-if="locations.entityData" label="Entidade"
                    class="p-button-secondary p-button-info p-button-sm p-button-auto"
                    :class="{'p-button-outlined': selectedPrefill !== 'entity'}"
                    @click.prevent="fillWith('entityData')" />
            </div>
        </div>
        <div class="p-fluid">
            <div class="p-field">
                <label>Nome *</label>
                <InputText :value="value.name" @input="update('name', $event)" />
            </div>
            <div class="p-field">
                <label>Morada *</label>
                <InputText :value="value.address" @input="update('address', $event)" />
            </div>
            <div class="p-formgrid p-grid">
                <div class="p-field p-col-3">
                    <label>Código País *</label>
                    <AutoComplete
                        :value="value.country"
                        @input="update('country', $event)"
                        @complete="searchCountry"
                        :suggestions="countryList"
                        field="code"
                        placeholder="Pesquise país"
                        @item-select="onCountrySelect"
                        style="width: 100%"
                        :dropdown="true"
                        appendTo="body"
                    >
                        <template #item="itemSlot">
                            <div>
                                <strong>{{ itemSlot.item.code }}</strong> - {{ itemSlot.item.name }}
                            </div>
                        </template>
                    </AutoComplete>
                </div>
                <div class="p-field p-col-4">
                    <label>C. Postal *</label>
                    <AutoComplete
                        :value="value.zip"
                        @input="update('zip', $event)"
                        @complete="lookupCity"
                        :suggestions="zipList"
                        field="code"
                        placeholder="Pesquise código postal"
                        @item-select="onZipSelect"
                        style="width: 100%"
                        :dropdown="false"
                        appendTo="body"
                    >
                        <template #item="itemSlot">
                            <div>
                                <strong>{{ itemSlot.item.code }}</strong> - {{ itemSlot.item.name }}
                            </div>
                        </template>
                    </AutoComplete>
                </div>
                <div class="p-field p-col-5">
                    <label>Cidade *</label>
                    <InputText :value="value.city" @input="update('city', $event)" />
                </div>
            </div>
            <div class="p-field">
                <label>Contacto Telefónico *</label>
                <InputText :value="value.phone" @input="update('phone', $event)" />
            </div>
            <div class="p-field">
                <label>E-mail</label>
                <InputText :value="value.email" @input="update('email', $event)" />
            </div>
            <div class="p-field">
                <label>Observações 1</label>
                <InputText :value="value.observations1" @input="update('observations1', $event)" />
            </div>
            <div class="p-field">
                <label>Observações 2</label>
                <InputText :value="value.observations2" @input="update('observations2', $event)" />
            </div>
        </div>
    </div>
</template>

<script>
import shipmentsService from '@/services/shipments.service';
import countryService from '@/services/country.service';

export default {
    name: 'NacexAddressBlock',
    props: {
        value: {
            type: Object,
            required: true,
            default: () => ({
                name: '', address: '', zip: '', city: '',
                phone: '', email: '', country: 'PT',
                observations1: '', observations2: ''
            })
        },
        title: { type: String, default: 'Endereço' },
        locations: {
            type: Object,
            default: () => ({
                clientData: null,
                entityData: null
            })
        },
        nacexAccounts: {
            type: Array,
            default: () => []
        },
        selectedAccountId: {
            type: [Number, String],
            default: null
        },
        prefillOnly: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            zipList: [],
            countryList: [],
            selectedPrefill: null
        };
    },
    watch: {
        locations: {
            handler() {
                this.detectSelectedPrefill();
            },
            deep: true,
            immediate: true
        },
        nacexAccounts: {
            handler() {
                this.detectSelectedPrefill();
            },
            deep: true,
            immediate: true
        },
        value: {
            handler(newVal, oldVal) {
                if (!oldVal) {
                    this.detectSelectedPrefill();
                }
            },
            deep: true,
            immediate: true
        }
    },
    methods: {
        update(field, value) {
            this.selectedPrefill = null;
            this.$emit('input', { ...this.value, [field]: value });
        },
        selectAccount(account) {
            this.$emit('select-account', account);
        },
        fillWithAccount(acc) {
            this.selectedPrefill = 'account-' + acc.id;
            this.$emit('input', {
                name: acc.name || '',
                address: acc.address || '',
                zip: acc.zip || '',
                city: acc.city || '',
                phone: acc.phone || '',
                email: acc.email || '',
                country: acc.country || 'PT',
                observations1: this.value?.observations1 || '',
                observations2: this.value?.observations2 || ''
            });
        },
        fillWith(locationType) {
            this.selectedPrefill = locationType === 'clientData' ? 'client' : 'entity';
            const loc = this.locations[locationType];
            if (!loc) return;
            this.$emit('input', {
                name: loc.name || '',
                address: loc.address || '',
                zip: loc.zip || '',
                city: loc.city || '',
                phone: loc.phone || '',
                email: loc.email || '',
                country: loc.country || 'PT',
                observations1: loc.observations1 || this.value?.observations1 || '',
                observations2: loc.observations2 || this.value?.observations2 || ''
            });
        },
        detectSelectedPrefill() {
            if (!this.value) return;

            // Check Nacex accounts (prefillOnly = true)
            if (this.prefillOnly && this.nacexAccounts && this.nacexAccounts.length > 0) {
                for (const acc of this.nacexAccounts) {
                    if (this.isAddressMatch(this.value, acc)) {
                        this.selectedPrefill = 'account-' + acc.id;
                        return;
                    }
                }
            }

            // Check Client
            if (this.locations?.clientData && this.isAddressMatch(this.value, this.locations.clientData)) {
                this.selectedPrefill = 'client';
                return;
            }

            // Check Entity
            if (this.locations?.entityData && this.isAddressMatch(this.value, this.locations.entityData)) {
                this.selectedPrefill = 'entity';
                return;
            }

            // No match
            this.selectedPrefill = null;
        },
        isAddressMatch(a, b) {
            if (!a || !b) return false;
            const normalize = (val) => String(val || '').trim().toLowerCase();
            return normalize(a.name) === normalize(b.name) &&
                   normalize(a.address) === normalize(b.address) &&
                   normalize(a.zip) === normalize(b.zip) &&
                   normalize(a.city) === normalize(b.city) &&
                   normalize(a.phone) === normalize(b.phone) &&
                   normalize(a.email) === normalize(b.email) &&
                   normalize(a.country) === normalize(b.country);
        },
        async lookupCity(event) {
            const zip = typeof event === 'object' ? event.query : this.value.zip;
            if (!zip || zip.length < 4) return;
            try {
                const response = await shipmentsService.lookupCities({ zip });
                if (response && response.cities) {
                    this.zipList = response.cities.map(c => ({ code: c.zip, name: c.city }));
                }
            } catch (error) {
                this.zipList = [];
            }
        },
        onZipSelect(event) {
            this.selectedPrefill = null;
            this.$emit('input', {
                ...this.value,
                zip: event.value.code,
                city: event.value.name
            });
        },
        async searchCountry(event) {
            try {
                const response = await countryService.queryCountry({ search: event.query });
                this.countryList = response.data;
            } catch (error) {
                this.countryList = [];
            }
        },
        onCountrySelect(event) {
            this.selectedPrefill = null;
            this.$emit('input', { ...this.value, country: event.value.code });
        }
    }
};
</script>

<style scoped>
.section-title {
    font-size: 1rem;
    font-weight: 600;
    color: #495057;
}

.p-field {
    margin-bottom: 1rem;
}

.p-field label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #495057;
}

.p-button-auto {
    width: auto;
}
</style>
